import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Curation } from '@/app/events/curateConfirm';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import type { DateWindow } from '@/lib/format/format';

export type PublishedEvent = Schemas['RaceEventDto'];
export type EventPage = Schemas['PagedResultOfRaceEventDto'];
export type EventFacts = Schemas['EventFactsDto'];

export interface EventsQuery {
  keyword?: string;
  /** Inclusive event dates; null matches nothing, so nothing is asked. */
  window?: DateWindow | null;
  latestFirst?: boolean;
  page?: number;
  size?: number;
}

export const eventKeys = {
  all: ['events'] as const,
  list: (query: EventsQuery) => [...eventKeys.all, 'list', query] as const,
  count: (window: DateWindow) => [...eventKeys.all, 'count', window] as const,
  detail: (id: string) => [...eventKeys.all, 'detail', id] as const,
  facts: (id: string) => [...eventKeys.all, 'facts', id] as const,
};

const empty = (query: EventsQuery): EventPage => ({
  items: [],
  totalCount: 0,
  pageNumber: query.page ?? 1,
  pageSize: query.size ?? 20,
});

/** The published catalog, name or city matched without diacritics, soonest first by default. */
export const eventsQuery = (query: EventsQuery) =>
  queryOptions({
    queryKey: eventKeys.list(query),
    queryFn: () =>
      query.window === null
        ? Promise.resolve(empty(query))
        : unwrap(
            api.GET('/api/events', {
              params: {
                query: {
                  Keyword: query.keyword,
                  StartDate: query.window?.start,
                  EndDate: query.window?.end,
                  PageNumber: query.page ?? 1,
                  PageSize: query.size ?? 20,
                  SortBy: 'DateTimeLocal',
                  SortDirection: query.latestFirst ? 'Descending' : 'Ascending',
                },
              },
            }),
          ),
    placeholderData: keepPreviousData,
  });

/** How many events fall in a date window, with no other filter. */
export const eventCountQuery = (window: DateWindow) =>
  queryOptions({
    queryKey: eventKeys.count(window),
    queryFn: () =>
      unwrap(
        api.GET('/api/events', {
          params: { query: { StartDate: window.start, EndDate: window.end, PageSize: 1 } },
        }),
      ).then((page) => page.totalCount),
    staleTime: 60_000,
  });

export const eventQuery = (id: string) =>
  queryOptions({
    queryKey: eventKeys.detail(id),
    queryFn: () =>
      unwrap(api.GET('/api/events/{raceEventId}', { params: { path: { raceEventId: id } } })),
    staleTime: 60_000,
  });

/** What an operator reads before cancelling or reinstating: the runners who marked interest, who owns it. */
export const eventFactsQuery = (id: string) =>
  queryOptions({
    queryKey: eventKeys.facts(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/events/{raceEventId}', { params: { path: { raceEventId: id } } }),
      ),
    staleTime: 0,
  });

/** An operator's cancellation or reinstatement of a curator event; runners who marked interest are told. */
export function useCurateEvent(curation: Curation) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      unwrap(
        curation === 'cancel'
          ? api.POST('/api/scraping/events/{raceEventId}/cancel', {
              params: { path: { raceEventId: eventId } },
            })
          : api.POST('/api/scraping/events/{raceEventId}/reinstate', {
              params: { path: { raceEventId: eventId } },
            }),
      ),
    // A refusal means the event moved on (another tab), so the lists read it again too.
    onSettled: () => queryClient.invalidateQueries({ queryKey: eventKeys.all }),
  });
}
